// 4.25 Revise draft on request (job F-06, migration 20261222070000; act group "drafts"): the draft coach on Drafts to
// approve (4.10). The score and versions come from crm_draft_coach (one read for every draft on the page, batched);
// a chip or Undo goes through act (revise_draft, restore_draft_version), which forces this person's business and
// actor. Types live here, not in lib/types.ts. The learning base adds a note (the owner's own instruction for one
// draft, instruction "note") and the draft's ledger, for "Why this draft".
import type { Crm } from "./crm";

export interface CoachReason {
  key: string;
  ok: boolean;
  text: string;
}

/** outbox.metadata.score (4.24, docs/CRM_TOOLS.md "F-05"). */
export interface CoachScore {
  score: number;
  band: "strong" | "good" | "weak";
  reasons: CoachReason[];
  signals?: { words?: number; preview?: string; reading_age?: number | null };
}

export interface CoachVersion {
  n: number;
  instruction: string;
  /** The owner's note, for a version made from one. */
  note: string | null;
  at: string | null;
  subject: string | null;
  body: string;
  score: number | null;
  band: string | null;
}

export interface CoachState {
  id: string;
  status: string;
  channel: string;
  subject: string | null;
  body: string;
  version: number;
  instruction: string;
  score: CoachScore | null;
  versions: CoachVersion[];
  /** The current version's note, when it was made from one. */
  note: string | null;
  /** outbox.metadata.ledger (lib/learning.ts readWhy reads it). */
  ledger: unknown;
}

export const CHIPS = [
  { key: "shorter", label: "Shorter" },
  { key: "warmer", label: "Warmer" },
  { key: "more_direct", label: "More direct" },
  { key: "another_fact", label: "Use another fact" },
] as const;
export type ChipKey = (typeof CHIPS)[number]["key"];
export type Instruction = ChipKey | "note" | "by_hand" | "original";
/** The longest note for one draft (act MAX_NOTE). */
export const MAX_NOTE = 300;

export const INSTRUCTION_LABELS: Record<string, string> = {
  original: "Original", shorter: "Shorter", warmer: "Warmer", more_direct: "More direct", another_fact: "Another fact",
  by_hand: "Your edit", note: "Your note",
};

export const label = (instruction: string) => INSTRUCTION_LABELS[instruction] ?? instruction.replaceAll("_", " ");

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? v as Record<string, unknown> : {});
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null);

export function readScore(v: unknown): CoachScore | null {
  const o = obj(v);
  const score = num(o.score);
  if (score === null) return null;
  const band = o.band === "strong" || o.band === "good" || o.band === "weak" ? o.band : score >= 80 ? "strong" : score >= 60 ? "good" : "weak";
  const reasons = (Array.isArray(o.reasons) ? o.reasons : []).map(obj)
    .filter((r) => typeof r.text === "string").map((r) => ({ key: String(r.key ?? ""), ok: r.ok === true, text: String(r.text) }));
  const s = obj(o.signals);
  return { score: Math.max(0, Math.min(100, Math.round(score))), band, reasons,
    signals: { words: num(s.words) ?? undefined, preview: str(s.preview) ?? undefined, reading_age: num(s.reading_age) } };
}

/** One draft's coach state, read tolerantly so a missing field never breaks the review. */
export function readCoach(v: unknown): CoachState | null {
  const o = obj(v);
  if (typeof o.id !== "string") return null;
  return {
    id: o.id, status: String(o.status ?? ""), channel: String(o.channel ?? ""), subject: str(o.subject), body: str(o.body) ?? "",
    version: num(o.version) ?? 1, instruction: str(o.instruction) ?? "original", score: readScore(o.score),
    note: str(o.note), ledger: o.ledger ?? null,
    versions: (Array.isArray(o.versions) ? o.versions : []).map(obj).filter((x) => num(x.n) !== null).map((x) => ({
      n: num(x.n)!, instruction: str(x.instruction) ?? "original", note: str(x.note), at: str(x.at), subject: str(x.subject), body: str(x.body) ?? "",
      score: num(x.score), band: str(x.band),
    })),
  };
}

// One read for every draft that asks in the same moment (a batch of 25 drafts is one call, not 25).
const pending = new WeakMap<Crm, { ids: Set<string>; waiters: ((m: Map<string, CoachState>) => void)[]; fail: ((e: unknown) => void)[] }>();

export function loadCoach(crm: Crm, id: string): Promise<CoachState | null> {
  return new Promise((resolve, reject) => {
    let p = pending.get(crm);
    if (!p) {
      const batch = { ids: new Set<string>(), waiters: [] as ((m: Map<string, CoachState>) => void)[], fail: [] as ((e: unknown) => void)[] };
      pending.set(crm, batch);
      p = batch;
      queueMicrotask(() => {
        pending.delete(crm);
        crm.read<unknown>("crm_draft_coach", { p_ids: [...batch.ids] }).then((rows) => {
          const m = new Map<string, CoachState>();
          for (const r of Array.isArray(rows) ? rows : []) {
            const c = readCoach(r);
            if (c) m.set(c.id, c);
          }
          for (const w of batch.waiters) w(m);
        }, (e) => {
          for (const f of batch.fail) f(e);
        });
      });
    }
    p.ids.add(id);
    p.waiters.push((m) => resolve(m.get(id) ?? null));
    p.fail.push(reject);
  });
}

export interface ReviseResult {
  saved: boolean;
  reasons: string[];
  state: CoachState | null;
  flagged: string[];
}

/** A chip, the owner's note (`note`) or their own words (`by_hand`): a new version, or the reasons it could not be made. */
export async function reviseDraft(crm: Crm, id: string, instruction: ChipKey | "note" | "by_hand", version: number,
                                  own?: { subject: string; body: string } | { note: string }): Promise<ReviseResult> {
  const r = obj(await crm.write<unknown>("revise_draft", { draft: id, instruction, version, ...(own ?? {}) }));
  const flagged = (Array.isArray(r.checks) ? r.checks : []).map(obj).filter((c) => c.ok === false)
    .map((c) => String(c.detail ?? c.rule ?? ""));
  if (r.saved !== true) {
    return { saved: false, reasons: (Array.isArray(r.reasons) ? r.reasons : []).map(String), state: null, flagged };
  }
  return { saved: true, reasons: [], state: readCoach(r), flagged };
}

/** Undo: an earlier version back. */
export async function restoreVersion(crm: Crm, id: string, version: number, current: number): Promise<CoachState | null> {
  return readCoach(await crm.write<unknown>("restore_draft_version", { draft: id, version, current }));
}

/** What a phone's inbox shows: the sender, the subject and the first line under it. */
export function phonePreview(subject: string | null, body: string): { subject: string; preview: string } {
  return { subject: (subject ?? "").replace(/\s+/g, " ").trim() || "(no subject)", preview: body.replace(/\s+/g, " ").trim().slice(0, 90) };
}

export const wordCount = (body: string) => body.match(/\S+/g)?.length ?? 0;
